import { useEffect, useMemo, useState } from 'react';

import date from 'date-and-time';
import { useLocation, useNavigate } from 'react-router';

import { EyeIcon } from '@heroicons/react/24/outline';
import { createColumnHelper } from '@tanstack/react-table';

import useFault from '../../data/fault/useFault';
import { Card } from '../../components/base/Card';
import { Select } from '../../components/base/Select';
import { Button } from '../../components/base/Button';
import { ClientTable } from '../../components/base/Table';
import useCustomers from '../../data/customer/useCustomers';
import { TextField } from '../../components/base/TextField';
import { usePageTitle } from '../../contexts/PageTitleContext';
import useAppliances from '../../data/appliance/useAppliances';
import { FaultHeatmap } from '../../components/Fault/FaultHeatmap';
import LoadingFallback from '../../components/shared/LoadingFallback';
import useUserNotifications from './../../data/user/useUserNotifications';
import { IPDNotification } from '../../types/PDNotification/PDNotification';
import { FaultDefinitionCategoryNames } from '../../types/Fault/FaultDefinition';

const columnHelper = createColumnHelper<IPDNotification>();

// Cell component for fault code
function FaultCodeCell({ faultDefinitionId }: { faultDefinitionId: string }) {
  const { fault } = useFault(faultDefinitionId);
  return (
    <LoadingFallback isLoading={fault.isLoading}>
      {fault.data ? fault.data[0].code : ''}
    </LoadingFallback>
  );
}

// Cell component for fault category
function FaultCategoryCell({
  faultDefinitionId,
}: {
  faultDefinitionId: string;
}) {
  const { fault } = useFault(faultDefinitionId);
  return (
    <LoadingFallback isLoading={fault.isLoading}>
      {fault.data ? FaultDefinitionCategoryNames[fault.data[0].category] : ''}
    </LoadingFallback>
  );
}

// Cell component for fault title
function FaultTitleCell({ faultDefinitionId }: { faultDefinitionId: string }) {
  const { fault } = useFault(faultDefinitionId);
  return (
    <LoadingFallback isLoading={fault.isLoading}>
      {fault.data ? fault.data[0].title : ''}
    </LoadingFallback>
  );
}

// Cell component for appliance name
function ApplianceNameCell({ applianceId }: { applianceId?: string }) {
  const { appliances } = useAppliances();
  const appliance = useMemo(() => {
    return appliances.data?.find((a) => a.id === applianceId);
  }, [appliances.data, applianceId]);

  return (
    <LoadingFallback isLoading={appliances.isLoading}>
      {appliance?.name ?? ''}
    </LoadingFallback>
  );
}

// Cell component for customer name
function CustomerNameCell({ customerId }: { customerId?: string }) {
  const { customers } = useCustomers();
  const customer = useMemo(() => {
    return customers.data?.find((c) => c.id === customerId);
  }, [customers.data, customerId]);

  return (
    <LoadingFallback isLoading={customers.isLoading}>
      {customerId ? (customer?.organisationName ?? '') : 'None'}
    </LoadingFallback>
  );
}

// Cell component for actions
function ActionsCell({
  faultDefinitionId,
  applianceId,
}: {
  faultDefinitionId: string;
  applianceId?: string;
}) {
  const navigate = useNavigate();
  const { fault } = useFault(faultDefinitionId);

  const handleViewFault = () => {
    if (fault.data?.[0]?.id) {
      navigate(fault.data[0].id, { state: { applianceId } });
    }
  };

  return (
    <EyeIcon
      className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
      title="View resource"
      onClick={handleViewFault}
    />
  );
}

// Cell component for formatted date
function DateCell({ dateStr }: { dateStr?: string }) {
  if (!dateStr) return null;

  return <span>{date.format(new Date(dateStr), 'DD/MM/YYYY HH:mm:ss')}</span>;
}

export default function FaultPage() {
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const location = useLocation();
  const applianceId = location.state?.applianceId;
  const { userNotifications } = useUserNotifications(true, applianceId);
  const { appliances } = useAppliances();
  const [selectedDate, setSelectedDate] = useState<Date>();

  // Column definitions for the ClientTable
  const columns = useMemo(
    () => [
      columnHelper.accessor('faultDefinitionId', {
        header: 'Code',
        cell: (info) => <FaultCodeCell faultDefinitionId={info.getValue()!} />,
      }),
      columnHelper.accessor('faultDefinitionId', {
        id: 'category',
        header: 'Category',
        cell: (info) => (
          <FaultCategoryCell faultDefinitionId={info.getValue()!} />
        ),
      }),
      columnHelper.accessor('faultDefinitionId', {
        id: 'title',
        header: 'Title',
        cell: (info) => <FaultTitleCell faultDefinitionId={info.getValue()!} />,
      }),
      columnHelper.accessor('applianceId', {
        header: 'Appliance',
        cell: (info) => <ApplianceNameCell applianceId={info.getValue()} />,
      }),
      columnHelper.accessor('customerId', {
        header: 'Customer',
        cell: (info) => <CustomerNameCell customerId={info.getValue()} />,
      }),
      columnHelper.accessor('timestamp', {
        header: 'Timestamp',
        cell: (info) => <DateCell dateStr={info.getValue()} />,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <ActionsCell
            faultDefinitionId={info.row.original.faultDefinitionId!}
            applianceId={info.row.original.applianceId}
          />
        ),
      }),
    ],
    []
  );

  const filteredData = useMemo(() => {
    if (!userNotifications.data) return [];
    if (!selectedDate) return userNotifications.data;

    // Create start and end of selected day in UTC
    const startOfDay = new Date(selectedDate);
    startOfDay.setUTCHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setUTCHours(23, 59, 59, 999);

    return userNotifications.data.filter((n) => {
      const notificationDate = new Date(n.timestamp);
      return notificationDate >= startOfDay && notificationDate <= endOfDay;
    });
  }, [userNotifications.data, selectedDate]);

  // Sort the data by timestamp (newest first)
  const sortedData = useMemo(() => {
    return [...filteredData].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [filteredData]);

  // Find the selected appliance (only if applianceId is provided)
  const appliance = useMemo(() => {
    if (!applianceId || !appliances.data) return null;
    return appliances.data.find((a) => a.id === applianceId);
  }, [appliances.data, applianceId]);

  // Determine if we should show appliance-specific loading
  const showApplianceLoading = applianceId && appliances.isLoading;

  // Determine the page title
  const pageTitle = useMemo(() => {
    if (applianceId) {
      if (appliances.isLoading) return 'Loading...';

      if (appliance) return `Faults for ${appliance.name}`;

      return 'Appliance Not Found';
    }

    return 'All Faults';
  }, [applianceId, appliance, appliances.isLoading]);

  useEffect(() => {
    setPageTitle('Faults');
  }, [setPageTitle]);

  return (
    <Card titleLoading={showApplianceLoading} title={pageTitle}>
      <div>
        <div className="mt-4">
          <LoadingFallback
            skeletonClasses="h-36 w-full"
            isLoading={userNotifications.isLoading}
          >
            <FaultHeatmap
              faults={userNotifications.data || []}
              onCellClick={(date) => {
                setSelectedDate(date);
              }}
            />
          </LoadingFallback>
        </div>
        <div className="mt-6 flex flex-row items-end space-x-4">
          <LoadingFallback
            skeletonClasses="h-10"
            isLoading={appliances.isLoading}
          >
            <Select
              label="Show faults for appliance"
              value={applianceId ?? ''}
              onChange={(e) => {
                const value = e.currentTarget.value;
                navigate('.', { state: { applianceId: value || undefined } });
              }}
            >
              <option value="">All Appliances</option>
              {appliances.data?.map((appliance) => (
                <option key={appliance.id} value={appliance.id}>
                  {appliance.name}
                </option>
              ))}
            </Select>
          </LoadingFallback>
          <TextField
            label="Show faults for date"
            type="date"
            value={selectedDate?.toISOString().split('T')[0] ?? ''}
            onChange={(e) => {
              setSelectedDate(
                e.currentTarget.value
                  ? new Date(e.currentTarget.value + 'T00:00:00.000Z')
                  : undefined
              );
            }}
          />
          <Button
            className="h-[42px]"
            disabled={!selectedDate}
            onClick={() => setSelectedDate(undefined)}
          >
            Clear date filter
          </Button>
        </div>
        <div className="mt-6">
          <ClientTable
            data={sortedData}
            columns={columns}
            isLoading={userNotifications.isLoading}
            enableGlobalFilter={false}
            enableSorting={true}
            enableColumnFilters={true}
            rowsPerPage={10}
            emptyMessage="No faults found"
          />
        </div>
      </div>
    </Card>
  );
}
